import { AppleOutlined, InboxOutlined, MessageOutlined, WhatsAppOutlined } from '@ant-design/icons'
import { CommunicationChannel } from '@community_dev/types/lib/api/CommunicationChannel'
import { useQuery } from '@tanstack/react-query'
import { Skeleton } from 'antd'
import dayjs from 'dayjs'
import { type ComponentType, type JSX, type ReactNode } from 'react'
import styled from 'styled-components'

import { DIVIDER, MUTED_TEXT, TEXT } from '../../colors'
import { COMMUNICATION_CHANNEL_LABEL } from '../General/constants'
import { SettingsCard } from '../Settings'
import { StatusBadge } from '../StatusBadge'

import { LATEST_CAMPAIGNS_MONTHS, toLatestCampaigns } from './campaignRows'

import { getRecentCampaigns } from 'api/campaigns'
import { QUERIES } from 'constants/queries'

// The list is sorted by send time, so drafts can fall outside the first page. A larger page leaves room to pick the latest five.
const RECENT_CAMPAIGNS_PAGE_SIZE = 25

const CHANNEL_ICONS: Record<CommunicationChannel, ComponentType<{ 'aria-label'?: string }>> = {
  [CommunicationChannel.SMS]: MessageOutlined,
  [CommunicationChannel.WHATS_APP]: WhatsAppOutlined,
  [CommunicationChannel.AMB]: AppleOutlined,
}

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 24px;
    text-align: left;
    white-space: nowrap;
  }

  th {
    color: ${MUTED_TEXT};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  td {
    border-top: 1px solid ${DIVIDER};
    color: ${TEXT};
    font-size: 14px;
  }

  td:first-child {
    width: 100%;
    max-width: 0;
  }
`

const StyledTableScroll = styled.div`
  overflow-x: auto;
`

const StyledCampaignName = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  .anticon {
    flex: none;
    color: ${MUTED_TEXT};
    font-size: 16px;
  }

  > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const StyledDate = styled.span`
  color: ${MUTED_TEXT};
`

const StyledMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 32px 24px;
  color: ${MUTED_TEXT};
  font-size: 14px;
  text-align: center;

  .anticon {
    font-size: 28px;
  }
`

const StyledLoading = styled.div`
  padding: 16px 24px;
`

export function LatestCampaigns({ clientId }: { clientId: string }): JSX.Element {
  const { data, isLoading, isError } = useQuery([QUERIES.CAMPAIGNS_RECENT, { clientId }], () =>
    getRecentCampaigns({ clientId, pageSize: RECENT_CAMPAIGNS_PAGE_SIZE }),
  )
  const campaigns = toLatestCampaigns(data?.data ?? [])

  function renderContent(): ReactNode {
    if (isLoading) {
      return (
        <StyledLoading>
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        </StyledLoading>
      )
    }
    if (isError) {
      return <StyledMessage>Could not load the campaigns of this client.</StyledMessage>
    }
    if (campaigns.length === 0) {
      return (
        <StyledMessage>
          <InboxOutlined aria-hidden="true" />
          <span>No campaigns in the past {LATEST_CAMPAIGNS_MONTHS} months.</span>
        </StyledMessage>
      )
    }
    return (
      <StyledTableScroll>
        <StyledTable>
          <thead>
            <tr>
              <th scope="col">Campaign</th>
              <th scope="col">Recipients</th>
              <th scope="col">Response %</th>
              <th scope="col">Clicks %</th>
              <th scope="col">Stop %</th>
              <th scope="col">Status</th>
              <th scope="col">Date</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => {
              const ChannelIcon = CHANNEL_ICONS[campaign.channel] ?? MessageOutlined
              return (
                <tr key={campaign.id}>
                  <td>
                    <StyledCampaignName title={campaign.name}>
                      <ChannelIcon aria-label={COMMUNICATION_CHANNEL_LABEL[campaign.channel] ?? campaign.channel} />
                      <span>{campaign.name}</span>
                    </StyledCampaignName>
                  </td>
                  <td>{campaign.recipients.toLocaleString('en-US')}</td>
                  <td>{campaign.responseRate}</td>
                  <td>{campaign.clickRate}</td>
                  <td>{campaign.stopRate}</td>
                  <td>
                    <StatusBadge tone={campaign.status.tone}>{campaign.status.label}</StatusBadge>
                  </td>
                  <td>
                    <StyledDate>
                      {campaign.dateLabel}{' '}
                      <time dateTime={campaign.date}>{dayjs(campaign.date).format('MMM D, YYYY')}</time>
                    </StyledDate>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </StyledTable>
      </StyledTableScroll>
    )
  }

  return (
    <SettingsCard extra={<StyledDate>Past {LATEST_CAMPAIGNS_MONTHS} months</StyledDate>} title="Latest Campaigns">
      {renderContent()}
    </SettingsCard>
  )
}
